import React, { useState, useEffect } from 'react';
import { Dialog } from '@mui/material';
import { fmt } from '../bms_helper';

// Design tokens
const T = {
  bg:       '#f2f6f3',
  white:    '#ffffff',
  ink:      '#0c1810',
  ink2:     '#1e3328',
  subtle:   '#4d6b58',
  muted:    '#8ba898',
  border:   '#dae6df',
  borderL:  '#eaf2ec',
  green:    '#0a6b3e',
  greenMd:  '#0d8a50',
  greenLt:  '#e4f5ec',
  red:      '#dc2626',
  redLt:    '#fee2e2',
  redBdr:   '#fca5a5',
};

function Field({ label, required, children, helper }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: T.subtle,
        letterSpacing: '0.2px', display: 'flex', alignItems: 'center', gap: 4,
      }}>
        {label}
        {required && <span style={{ color: T.red }}>*</span>}
      </div>
      {children}
      {helper && <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{helper}</div>}
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div style={{
      background: T.white,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      padding: '14px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: T.ink, letterSpacing: '-0.2px' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// Styled number input — no suffix inside the box, formatted value shown as helper
function NumberInput({ value, onChange, placeholder, min, max, step, suffix }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        type="number"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min} max={max} step={step}
        onFocus={() => setFocused(true)}
        onBlur={()  => setFocused(false)}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: suffix ? '9px 52px 9px 12px' : '9px 12px',
          fontSize: 13,
          color: T.ink,
          background: T.white,
          border: `1.5px solid ${focused ? T.green : T.border}`,
          borderRadius: 9,
          outline: 'none',
          transition: 'border-color 150ms, box-shadow 150ms',
          boxShadow: focused ? '0 0 0 3px rgba(10,107,62,0.1)' : 'none',
          fontFamily: 'inherit',
          // hide browser number spinners
          MozAppearance: 'textfield',
        }}
      />
      {suffix && (
        <span style={{
          position: 'absolute', right: 10, top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 11, fontWeight: 700, color: T.green,
          pointerEvents: 'none',
        }}>
          {suffix}
        </span>
      )}
    </div>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <div style={{
      background: highlight ? T.green : T.greenLt,
      border: `1px solid ${highlight ? T.green : T.border}`,
      borderRadius: 10,
      padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 10,
        color: highlight ? 'rgba(255,255,255,0.75)' : T.subtle,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 600,
        marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 15,
        fontWeight: 700,
        color: highlight ? T.white : T.ink2,
        letterSpacing: '-0.2px',
      }}>
        {value}
      </div>
    </div>
  );
}

export default function EditCommissionDialog({ open, onClose, booking, onSave }) {
  const [loading, setLoading]         = useState(false);
  const [flash, setFlash]             = useState(null);
  const [flashMsg, setFlashMsg]       = useState('');
  const [formData, setFormData]       = useState({ commission_rate: 0, commission_amount: 0, other_incentives: [] });
  const [newIncentive, setNewIncentive] = useState({ type: '', amount: '' });

  const sale_order           = booking?.sale_order;
  const commission_breakdown = booking?.commission_breakdown;
  const unit                 = booking?.unit;
  const basePrice            = Number(sale_order?.base_price ?? unit?.base_price ?? unit?.price) || 0;
  const currentRateFromBreakdown   = Number(commission_breakdown?.base_commission_rate) || 0;
  const savedCommission            = Number(commission_breakdown?.display_commission);
  const currentCommissionDisplay   = savedCommission > 0
    ? savedCommission
    : (basePrice > 0 ? Math.round((basePrice * currentRateFromBreakdown) / 100) : 0);

  useEffect(() => {
    if (open && booking) {
      const rate        = Number(commission_breakdown?.base_commission_rate) || 0;
      const savedAmount = Number(commission_breakdown?.display_commission);
      const derived     = basePrice > 0 ? Math.round((basePrice * rate) / 100) : 0;
      setFormData({
        commission_rate:   rate,
        commission_amount: savedAmount > 0 ? savedAmount : derived,
        other_incentives:  commission_breakdown?.other_commission || [],
      });
    }
  }, [open, booking, basePrice]);

  const handleRateChange = (e) => {
    const raw  = e.target.value.replace(/^0+(?=\d)/, '');
    const rate = parseFloat(raw) || 0;
    setFormData(p => ({
      ...p,
      commission_rate:   raw === '' ? '' : rate,
      commission_amount: Math.round((basePrice * rate) / 100),
    }));
  };

  const handleAmountChange = (e) => {
    const raw    = e.target.value.replace(/^0+(?=\d)/, '');
    const amount = parseFloat(raw) || 0;
    const rate   = basePrice > 0 ? Math.round((amount * 100) / basePrice * 100) / 100 : 0;
    setFormData(p => ({
      ...p,
      commission_amount: raw === '' ? '' : amount,
      commission_rate:   rate,
    }));
  };

  const showFlash = (type, msg) => {
    setFlash(type);
    setFlashMsg(msg);
    setTimeout(() => setFlash(null), 3000);
  };

  const handleAddIncentive = () => {
    if (!newIncentive.type.trim() || !newIncentive.amount) {
      showFlash('error', 'Fill in both incentive type and amount');
      return;
    }
    const amount = parseFloat(newIncentive.amount);
    if (isNaN(amount) || amount <= 0) {
      showFlash('error', 'Enter a valid positive amount');
      return;
    }
    setFormData(p => ({
      ...p,
      other_incentives: [...p.other_incentives, { name: newIncentive.type.trim(), amount }],
    }));
    setNewIncentive({ type: '', amount: '' });
  };

  const handleRemove = (i) =>
    setFormData(p => ({ ...p, other_incentives: p.other_incentives.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (formData.commission_rate < 0 || formData.commission_amount < 0) {
      showFlash('error', 'Values cannot be negative');
      return;
    }
    setLoading(true);
    try {
      await onSave({
        base_commission_rate: formData.commission_rate,
        commission_amount:    formData.commission_amount,
        other_incentives:     formData.other_incentives,
      });
      showFlash('success', '✓ Commission saved successfully');
      setTimeout(onClose, 1200);
    } catch {
      showFlash('error', 'Failed to save commission');
    } finally {
      setLoading(false);
    }
  };

  const totalIncentives = formData.other_incentives.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const totalCommission = (Number(formData.commission_amount) || 0) + totalIncentives;

  const inputBase = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '9px 12px',
    fontSize: 13,
    color: T.ink,
    background: T.white,
    border: `1.5px solid ${T.border}`,
    borderRadius: 9,
    outline: 'none',
    fontFamily: 'inherit',
  };

  return (
    <>
      <style>{`
        @keyframes ecSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ec-body { animation: ecSlideUp 220ms ease both; }
        .ec-body::-webkit-scrollbar       { width: 5px; }
        .ec-body::-webkit-scrollbar-thumb { background: #ccddd5; border-radius: 99px; }
        .ec-body::-webkit-scrollbar-track { background: transparent; }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
      `}</style>

      {/* Flash toast */}
      {flash && (
        <div style={{
          position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10000, padding: '10px 20px', borderRadius: 10,
          fontSize: 13, fontWeight: 600,
          background: flash === 'success' ? T.green : T.red,
          color: T.white,
          boxShadow: flash === 'success'
            ? '0 4px 16px rgba(10,107,62,0.4)'
            : '0 4px 16px rgba(220,38,38,0.35)',
        }}>
          {flashMsg}
        </div>
      )}

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: '16px', overflow: 'hidden', m: 0,
            width: '100%', maxWidth: 560, maxHeight: '92vh',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            border: `1.5px solid ${T.border}`,
          },
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '92vh', maxHeight: '92vh' }}>

          {/* ── Header ───────────────────────────────────────────────── */}
          <div style={{
            background: 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
            padding: '18px 22px', flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              width: 42, height: 42, borderRadius: 11, flexShrink: 0,
              background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            }}>💰</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.2px' }}>Edit Commission</div>
              <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>Adjust rates & incentives for this booking</div>
            </div>
            <button
              type="button" onClick={onClose}
              style={{
                width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                background: 'rgba(255,255,255,0.18)', border: 'none',
                color: '#fff', fontSize: 16, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >✕</button>
          </div>

          {/* ── Scrollable body ──────────────────────────────────────── */}
          <div
            className="ec-body"
            style={{
              flex: 1, minHeight: 0,          // ← FIX: prevents flex overflow clipping
              overflowY: 'auto',
              padding: 14,
              display: 'flex', flexDirection: 'column', gap: 10,
              background: T.bg,
            }}
          >

            {/* 1 — Sales Order Summary */}
            <Section icon="📋" title="Sales Order Summary">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <SummaryCard label="Base Price"          value={`₹${fmt(basePrice)}`} />
                <SummaryCard label="Current Rate"        value={`${currentRateFromBreakdown}%`} />
                <SummaryCard label="Current Commission"  value={`₹${fmt(currentCommissionDisplay)}`} />
              </div>
            </Section>

            {/* 2 — Commission Settings */}
            <Section icon="⚙️" title="Commission Settings">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

                {/* Rate */}
                <Field
                  label="Commission Rate" required
                  helper={`Percentage of base price (₹${fmt(basePrice)})`}
                >
                  <NumberInput
                    value={formData.commission_rate}
                    onChange={handleRateChange}
                    placeholder="0"
                    min={0} max={100} step={0.01}
                    suffix="% rate"
                  />
                </Field>

                {/* Amount — FIX: show formatted value as helper, not as overlapping suffix */}
                <Field
                  label="Commission Amount" required
                  helper={formData.commission_amount ? `= ₹${fmt(formData.commission_amount)}` : undefined}
                >
                  <NumberInput
                    value={formData.commission_amount}
                    onChange={handleAmountChange}
                    placeholder="0"
                    min={0} step={100}
                    suffix="₹"
                  />
                </Field>

              </div>
            </Section>

            {/* 3 — Other Incentives */}
            <Section icon="🎁" title="Other Incentives">
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                paddingBottom: 12, borderBottom: `1px solid ${T.borderL}`,
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.ink2 }}>Add Incentive</span>
                <span style={{
                  fontSize: 11, color: T.muted,
                  background: '#f3f4f6', padding: '2px 8px',
                  borderRadius: 20, fontWeight: 500,
                }}>Optional</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                <Field label="Incentive Type">
                  <input
                    type="text"
                    value={newIncentive.type}
                    onChange={(e) => setNewIncentive(p => ({ ...p, type: e.target.value }))}
                    placeholder="e.g. Performance Bonus"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddIncentive()}
                    style={inputBase}
                  />
                </Field>
                <Field label="Amount (₹)">
                  <input
                    type="number"
                    min={0}
                    value={newIncentive.amount}
                    onChange={(e) => setNewIncentive(p => ({ ...p, amount: e.target.value }))}
                    placeholder="0"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddIncentive()}
                    style={inputBase}
                  />
                </Field>
                <button
                  type="button"
                  onClick={handleAddIncentive}
                  style={{
                    height: 38, padding: '0 16px',
                    background: T.green, border: 'none', borderRadius: 9,
                    color: '#fff', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                    fontFamily: 'inherit',
                  }}
                >+ Add</button>
              </div>

              {formData.other_incentives.length > 0 && (
                <div style={{ border: `1.5px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                  {formData.other_incentives.map((inc, i) => (
                    <div key={i} style={{
                      display: 'grid', gridTemplateColumns: '1fr auto auto',
                      alignItems: 'center', padding: '10px 14px', gap: 12,
                      borderBottom: i < formData.other_incentives.length - 1 ? `1px solid ${T.borderL}` : 'none',
                      background: T.white,
                    }}>
                      <span style={{ fontSize: 13, color: T.ink2, fontWeight: 500 }}>{inc.name}</span>
                      <span style={{ fontSize: 13, color: T.green, fontWeight: 600 }}>₹{fmt(inc.amount)}</span>
                      <button
                        type="button" onClick={() => handleRemove(i)}
                        style={{
                          width: 28, height: 28, borderRadius: 8,
                          background: T.redLt, border: `1px solid ${T.redBdr}`,
                          color: T.red, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
                        }}
                      >✕</button>
                    </div>
                  ))}
                  {/* Incentives total row */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr auto auto',
                    padding: '10px 14px', gap: 12,
                    background: T.greenLt, borderTop: `1px solid ${T.border}`,
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.green, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total Incentives</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.green }}>₹{fmt(totalIncentives)}</span>
                    <span />
                  </div>
                </div>
              )}
            </Section>

            {/* 4 — Final Summary */}
            <Section icon="🏦" title="Final Commission Summary">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <SummaryCard label="Base Commission"  value={`₹${fmt(formData.commission_amount)}`} />
                <SummaryCard label="Other Incentives" value={`₹${fmt(totalIncentives)}`} />
                <SummaryCard label="Total Commission" value={`₹${fmt(totalCommission)}`} highlight />
              </div>
            </Section>

          </div>

          {/* ── Footer ───────────────────────────────────────────────── */}
          <div style={{
            padding: '13px 16px', flexShrink: 0,
            background: T.white, borderTop: `1px solid ${T.border}`,
            display: 'flex', justifyContent: 'flex-end', gap: 10,
          }}>
            <button
              type="button" onClick={onClose} disabled={loading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 9,
                background: T.redLt, border: `1.5px solid ${T.redBdr}`,
                color: T.red, fontSize: 12.5, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1, fontFamily: 'inherit',
              }}
            >✕ Cancel</button>

            <button
              type="button" onClick={handleSave} disabled={loading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 20px', borderRadius: 9,
                background: loading ? '#7ab898' : 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
                border: 'none', color: '#fff', fontSize: 12.5, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 2px 10px rgba(10,107,62,0.28)',
                fontFamily: 'inherit',
              }}
            >
              {loading ? '⏳ Saving…' : '💾 Save Changes'}
            </button>
          </div>

        </div>
      </Dialog>
    </>
  );
}
